import { generatePath } from 'react-router-dom';

// 타입 정의
type PathParams = Record<string, string | number>;

// 경로 빌더 인터페이스 - 모든 경로가 동일한 인터페이스 제공
interface PathBuilder<TParams extends PathParams = never> {
	path: string;
	to: [TParams] extends [never] ? () => string : (params: TParams) => string;
}

// 경로 세그먼트를 결합하여 정규화된 경로 생성
const joinPaths = (...segments: string[]): string => {
	const path = segments
		.map((segment) => segment.trim().replace(/^\/+|\/+$/g, ''))
		.filter(Boolean)
		.join('/');
	return `/${path}`;
};

/**
 * 타입 안전한 경로 빌더 생성
 * - 파라미터가 없는 경로: to() 함수 인자 없음
 * - 파라미터가 있는 경로: to(params) 형태로 타입 안전하게 사용
 */
function createPathBuilder<TParams extends PathParams = never>(pathTemplate: string): PathBuilder<TParams> {
	return {
		path: pathTemplate,
		to: ((params?: TParams) => {
			if (!params) return pathTemplate;
			const stringParams = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
				acc[key] = String(value);
				return acc;
			}, {});

			return generatePath(pathTemplate, stringParams);
		}) as PathBuilder<TParams>['to'],
	};
}

// 중첩 경로 빌더 생성 헬퍼
function nestPaths<T>(root: string, builder: (rootPath: string) => T): T {
	return builder(root);
}

// 루트 경로 상수
export const ROOTS = {
	LOGIN: '/login',
	FIND: '/find',
	HOME: '/',
	DASHBOARD: '/dashboard',
	MAIN_SECTION: '/main',
	USER: '/user',
	CONTEST: '/contest',
	PROGRAM: '/program',
	STORE: '/store',
	OPERATION: '/operation',
	PAYMENTS: '/payments', // To do : 결제·주문 관리 추가
	SYSTEM: '/system',
	MY: '/my',
} as const;

// 경로 정의
export const paths = {
	// 기본 경로
	login: createPathBuilder(ROOTS.LOGIN),
	home: createPathBuilder(ROOTS.HOME),
	dashboard: createPathBuilder(ROOTS.DASHBOARD),

	// 계정 찾기
	find: nestPaths(ROOTS.FIND, (root) => ({
		email: createPathBuilder(joinPaths(root, 'email')),
		password: createPathBuilder(joinPaths(root, 'password')),
	})),
	// 내 정보
	my: nestPaths(ROOTS.MY, (root) => ({
		profile: createPathBuilder(joinPaths(root, 'profile')),
	})),
	// 메인 섹션 관리
	mainSection: nestPaths(ROOTS.MAIN_SECTION, (root) => ({
		// 최상단 배너
		topBanner: {
			list: createPathBuilder(joinPaths(root, 'top-banner', 'list')),
			new: createPathBuilder(joinPaths(root, 'top-banner', 'new')),
			edit: createPathBuilder<{ bannerId: string }>(joinPaths(root, 'top-banner', ':bannerId', 'edit')),
		},
		// 명예의 전당
		hallOfFame: {
			list: createPathBuilder(joinPaths(root, 'hall-of-fame')),
		},
		// 추천 러닝 용품
		runningGear: {
			list: createPathBuilder(joinPaths(root, 'running-gear')),
		},
		// 공지사항
		notice: {
			list: createPathBuilder(joinPaths(root, 'notice', 'list')),
			new: createPathBuilder(joinPaths(root, 'notice', 'new')),
			edit: createPathBuilder<{ noticeId: string }>(joinPaths(root, 'notice', ':noticeId', 'edit')),
			detail: createPathBuilder<{ noticeId: string }>(joinPaths(root, 'notice', ':noticeId')),
		},
		// 자주 묻는 질문
		faq: {
			list: createPathBuilder(joinPaths(root, 'faq', 'list')),
			new: createPathBuilder(joinPaths(root, 'faq', 'new')),
			edit: createPathBuilder<{ faqId: string }>(joinPaths(root, 'faq', ':faqId', 'edit')),
			detail: createPathBuilder<{ faqId: string }>(joinPaths(root, 'faq', ':faqId')),
		},
	})),

	// 회원 관리
	user: nestPaths(ROOTS.USER, (root) => ({
		// 회원 목록 CRUD
		list: createPathBuilder(joinPaths(root, 'list')),
		new: createPathBuilder(joinPaths(root, 'new')),
		detail: createPathBuilder<{ userId: string }>(joinPaths(root, ':userId')),
		edit: createPathBuilder<{ userId: string }>(joinPaths(root, ':userId', 'edit')),

		withdrawnUser: {
			list: createPathBuilder(joinPaths(root, 'withdrawn-user', 'list')),
			detail: createPathBuilder<{ userId: string }>(joinPaths(root, 'withdrawn-user', ':userId')),
		},
		email: {
			list: createPathBuilder(joinPaths(root, 'email', 'list')),
			send: createPathBuilder(joinPaths(root, 'email', 'send')),
		},
		kakao: {
			list: createPathBuilder(joinPaths(root, 'kakao', 'list')),
			send: createPathBuilder(joinPaths(root, 'kakao', 'send')),
		},
	})),

	// 대회 관리
	contest: nestPaths(ROOTS.CONTEST, (root) => ({
		// 대회 CRUD
		list: createPathBuilder(joinPaths(root, 'list')),
		new: createPathBuilder(joinPaths(root, 'new')),
		edit: createPathBuilder<{ contestId: string }>(joinPaths(root, ':contestId', 'edit')),
		detail: createPathBuilder<{ contestId: string }>(joinPaths(root, ':contestId')),
		photos: createPathBuilder<{ contestId: string }>(joinPaths(root, ':contestId', 'photos')),
		additionalRecruitment: createPathBuilder<{ contestId: string }>(joinPaths(root, ':contestId', 'add-recruit')),

		// 참가자 관리
		participant: {
			list: createPathBuilder(joinPaths(root, 'participant', 'list')),
			detail: createPathBuilder<{ contestId: string }>(joinPaths(root, 'participant', ':contestId')),
		},

		// 기록 관리
		record: {
			list: createPathBuilder(joinPaths(root, 'record', 'list')),
		},

		// 이메일 발송
		email: {
			list: createPathBuilder(joinPaths(root, 'email', 'list')),
			send: createPathBuilder(joinPaths(root, 'email', 'send')),
		},

		// 카카오 발송
		kakao: {
			list: createPathBuilder(joinPaths(root, 'kakao', 'list')),
			send: createPathBuilder(joinPaths(root, 'kakao', 'send')),
		},
	})),

	// 프로그램 관리
	program: nestPaths(ROOTS.PROGRAM, (root) => ({
		// 프로그램 CRUD
		list: createPathBuilder(joinPaths(root, 'list')),
		new: createPathBuilder(joinPaths(root, 'new')),
		detail: createPathBuilder<{ programId: string }>(joinPaths(root, ':programId')),
		edit: createPathBuilder<{ programId: string }>(joinPaths(root, ':programId', 'edit')),

		// 회차 관리
		session: {
			list: createPathBuilder(joinPaths(root, 'session', 'list')),
			new: createPathBuilder(joinPaths(root, 'session', 'new')),
			detail: createPathBuilder<{ programId: string; sessionId: string }>(
				joinPaths(root, ':programId', 'session', ':sessionId'),
			),
			edit: createPathBuilder<{ programId: string; sessionId: string }>(
				joinPaths(root, ':programId', 'session', ':sessionId', 'edit'),
			),
		},
		email: {
			list: createPathBuilder(joinPaths(root, 'email', 'list')),
			send: createPathBuilder(joinPaths(root, 'email', 'send')),
		},
		kakao: {
			list: createPathBuilder(joinPaths(root, 'kakao', 'list')),
			send: createPathBuilder(joinPaths(root, 'kakao', 'send')),
		},
	})),

	// 운영 관리
	operation: nestPaths(ROOTS.OPERATION, (root) => ({
		caption: {
			list: createPathBuilder(joinPaths(root, 'caption', 'list')),
			new: createPathBuilder(joinPaths(root, 'caption', 'new')),
		},
		news: {
			list: createPathBuilder(joinPaths(root, 'news', 'list')),
			new: createPathBuilder(joinPaths(root, 'news', 'new')),
			detail: createPathBuilder<{ newsId: string }>(joinPaths(root, 'news', ':newsId')),
			edit: createPathBuilder<{ newsId: string }>(joinPaths(root, 'newsId', ':newsId', 'edit')),
		},
		instagram: {
			list: createPathBuilder(joinPaths(root, 'instagram', 'list')),
			new: createPathBuilder(joinPaths(root, 'instagram', 'new')),
			detail: createPathBuilder<{ instagramId: string }>(joinPaths(root, 'instagram', ':instagramId')),
			edit: createPathBuilder<{ instagramId: string }>(joinPaths(root, 'instagram', ':instagramId', 'edit')),
		},
		youtube: {
			list: createPathBuilder(joinPaths(root, 'youtube', 'list')),
			new: createPathBuilder(joinPaths(root, 'youtube', 'new')),
			detail: createPathBuilder<{ youtubeId: string }>(joinPaths(root, 'youtube', ':youtubeId')),
			edit: createPathBuilder<{ youtubeId: string }>(joinPaths(root, 'youtube', ':youtubeId', 'edit')),
		},
		privacyPolicy: {
			list: createPathBuilder(joinPaths(root, 'privacy-policy', 'list')),
			new: createPathBuilder(joinPaths(root, 'privacy-policy', 'new')),
			detail: createPathBuilder<{ policyId: string }>(joinPaths(root, 'privacy-policy', ':policyId')),
		},
		termsOfService: {
			list: createPathBuilder(joinPaths(root, 'terms-of-service', 'list')),
			new: createPathBuilder(joinPaths(root, 'terms-of-service', 'new')),
			detail: createPathBuilder<{ termsId: string }>(joinPaths(root, 'terms-of-service', ':termsId')),
		},
	})),

	// 스토어 관리
	// 프로그램 관리
	store: nestPaths(ROOTS.STORE, (root) => ({
		// 상품 관리
		list: createPathBuilder(joinPaths(root, 'list')),
		new: createPathBuilder(joinPaths(root, 'new')),
		detail: createPathBuilder<{ productId: string }>(joinPaths(root, ':productId')),
		edit: createPathBuilder<{ productId: string }>(joinPaths(root, ':productId', 'edit')),

		// 주문 관리
		order: {
			list: createPathBuilder(joinPaths(root, 'order', 'list')),
			new: createPathBuilder(joinPaths(root, 'order', 'new')),
			detail: createPathBuilder<{ orderId: string }>(joinPaths(root, ':orderId')),
		},
		// 상품 문의
		inquiry: {
			list: createPathBuilder(joinPaths(root, 'inquiry', 'list')),
		},
		// 리뷰 관리
		review: {
			list: createPathBuilder(joinPaths(root, 'review', 'list')),
		},
	})),

	// To do : 결제·주문 관리
	payments: nestPaths(ROOTS.PAYMENTS, (root) => ({
		// 대회 결제
		contest: {
			list: createPathBuilder(joinPaths(root, 'contest', 'list')),
			detail: createPathBuilder<{ paymentId: string }>(joinPaths(root, 'contest', ':paymentId')),
		},
		// 프로그램 결제
		program: {
			list: createPathBuilder(joinPaths(root, 'program', 'list')),
			detail: createPathBuilder<{ paymentId: string }>(joinPaths(root, 'program', ':paymentId')),
		},
		// 스토어 결제
		store: {
			list: createPathBuilder(joinPaths(root, 'store', 'list')),
			detail: createPathBuilder<{ paymentId: string }>(joinPaths(root, 'store', ':paymentId')),
		},
	})),

	system: nestPaths(ROOTS.SYSTEM, (root) => ({
		admin: {
			list: createPathBuilder(joinPaths(root, 'admin', 'list')),
			new: createPathBuilder(joinPaths(root, 'admin', 'new')),
			detail: createPathBuilder<{ adminId: string }>(joinPaths(root, 'admin', ':adminId')),
			edit: createPathBuilder<{ adminId: string }>(joinPaths(root, 'admin', ':adminId', 'edit')),
		},
		log: {
			list: createPathBuilder(joinPaths(root, 'log', 'list')),
		},
	})),
} as const;

/*
// 1. 파라미터가 없는 경로
navigate(paths.contest.list.path); // router에서 사용
navigate(paths.contest.list.to()); // 네비게이션에서 사용

// 2. 파라미터가 있는 경로
<Route path={paths.contest.detail.path} /> // "/contest/:contestId"
navigate(paths.contest.detail.to({ contestId: '123' })) // "/contest/123"

// 3. 중첩된 경로
navigate(paths.program.session.detail.to({
	programId: 'prog-1',
	sessionId: 'sess-1'
})) // "/program/prog-1/session/sess-1"

// 4. 타입 안전성
paths.contest.detail.to({ contestId: '123' }) // ✅ 정상
paths.contest.detail.to({ wrongParam: '123' }) // ❌ 타입 에러
paths.contest.list.to({ contestId: '123' }) // ❌ 타입 에러 (파라미터 불필요)
*/
